<template>
  <form class="absoluteLabelsForm  contactForm__Form" @submit.prevent="submitForm">
    <h2>{{ $t('contactForm.formTitle') }}</h2>
    <p class="contactForm__FormDisclaimer">{{ $t('contactForm.formDisclaimer') }}</p>
    <div class="contactForm__FormSection">
      <h3>{{ $t('contactForm.formAccommodation') }}</h3>
      <SelectField
        :ref="addField"
        v-model="formData.accommodation"
        :options="properties"
        text-key="name"
        value-key="id"
        autocomplete
        :label="$t('availabilityContact.propertyName')"
        :validate="{ type: 'required', message: { required: $t('formErrors.required') } }"
        :errors="formErrors?.details?.accommodation"
      />
      <div class="formField textField" :class="{'hasValue': period, 'isError': formErrors?.period}">
        <div class="contactForm__FormDateField" @click="toggleDateModal">
          <label class="form__Label" v-text="$t('availabilityContact.chosenPeriod')" />
          <div class="textField__Wrapper">
            <p>{{period}}</p>
            <button v-if="period" type="button" @click="clearDate" aria-label="Clear"><Icon name="clear" /></button>
          </div>
        </div>
        <div v-if="formErrors?.period" class="form__Errors">
          <span class="form__Error">{{ formErrors?.period }}</span>
        </div>
      </div>
      <SelectField
        v-model="formData.adults_count"
        :label="$t('availabilityContact.numberOfAdults')"
        :options="adultOptions"
        text-key="text"
        class="availabilityContact__FormGuestsDropdown"
      />
      <SelectField
        v-model="children"
        :label="$t('availabilityContact.numberOfChildren')"
        :options="childrenOptions"
        text-key="text"
        class="availabilityContact__FormGuestsDropdown hasValue"
      />
      <div class="contactForm__FormChildrenAge" v-if="children >= 1">
        <SelectField
          v-for="(child, index) in children"
          :key="index"
          :label="$t('availabilityContact.childAge', index + 1)"
          v-model="formData.children_age[index]"
          :options="childrenAge"
          text-key="value"
          value-key="value"
          :errors="formErrors?.details?.children_age?.[index]"
        />
      </div>
    </div>
    <div class="contactForm__FormSection">
      <h3>{{ $t('contactForm.formContact') }}</h3>
      <TextField :ref="addField" v-model="formData.first_name" :label="`${$t('forms.firstName')} *`" :errors="formErrors?.details?.first_name" :validate="{type: ['required', 'maxlength'], maxlength: 30, message: { required: $t('formErrors.required'), maxlength: $t('formErrors.maxLength', 30) }}"/>
      <TextField :ref="addField" v-model="formData.last_name" :label="`${$t('forms.lastName')} *`" :errors="formErrors?.details?.last_name" :validate="{type: ['required', 'maxlength'], maxlength: 30, message: { required: $t('formErrors.required'), maxlength: $t('formErrors.maxLength', 30) }}"/>
      <TextField :ref="addField" v-model="formData.email" type="email" :label="`${$t('forms.email')} *`" :errors="formErrors?.details?.email" :validate="{ type: ['required', 'email'], message: { required: $t('formErrors.required'), email: $t('formErrors.email') } }"/>
      <span>{{ $t('availabilityContact.confirmationEmail') }}</span>
      <TextField
        :ref="addField"
        v-model="formData.confirm_email"
        type="email"
        :label="`${$t('forms.confirmEmail')} *`"
        :validate="{
          type: ['required', 'email', 'sameAs'],
          compareValue: formData.email,
          message: { required: $t('formErrors.required'), email: $t('formErrors.email'), sameAs: $t('formErrors.sameAsEmail') },
        }"
      />
      <TextField v-model="formData.address" :label="$t('forms.streetAddress')" :errors="formErrors?.details?.address" />
      <TextField v-model="formData.city" :label="$t('forms.city')" :errors="formErrors?.details?.city" />
      <SelectField
        v-model="formData.country"
        :options="countriesList"
        :label="$t('forms.country')"
        text-key="name"
        value-key="code"
        autocomplete
        :errors="formErrors?.details?.country" />
      <TextField v-model="formData.zip" :label="$t('forms.postalCode')" :errors="formErrors?.details?.zip" />
      <PhoneNumberField v-model:numberValue="phoneNumber.phone_number" v-model:codeValue="phoneNumber.dial_code" :label="$t('forms.mobile')"/>
      <TextareaField
        v-model="formData.comment"
        :label="$t('availabilityContact.specialRequestLabel')"
        :placeholder="$t('availabilityContact.specialRequestPlaceholder')"
      />
    </div>
    <div class="contactForm__FormSection">
      <CheckboxField
        :ref="addConditionalField"
        v-model="terms"
      >
        <i18n-t tag="p" scope="global" keypath="availabilityContact.checkbox">
          <template #linkConditions>
            <nuxt-link :to="link('GeneralConditions')" target="_blank">{{ $t('availabilityContact.linkConditions') }}</nuxt-link>
          </template>
          <template #linkPrivacy>
            <nuxt-link :to="link('PrivacyPolicy')" target="_blank">{{ $t('availabilityContact.linkPrivacy') }}</nuxt-link>
          </template>
        </i18n-t>
      </CheckboxField>
    </div>
    <Button :disabled="isButtonDisabled" type="button" class="contactForm__FormSubmit" medium full-width>{{ $t('submit') }}</Button>
    <Loader :active="isLoading" noText></Loader>
  </form>

  <Teleport to="body">
    <div class="contactForm__Datepicker" v-if="dateModal">
      <button class="contactForm__DatepickerClose" type="button" @click="toggleDateModal">
        Close
        <Icon name="close" />
      </button>
      <VueDatePicker
        class="reservationPanel__Calendar"
        v-model="datepickerDates"
        range
        :multi-calendars="{ count: 2 }"
        :clearable="false"
        :auto-apply="true"
        :enable-time-picker="false"
        month-name-format="long"
        :format="formatDatepickerDate"
        :hide-offset-dates="true"
        :min-date="new Date()"
        inline
        @update:model-value="updateDates">
        <template #arrow-left>
          <Icon name="arrow-left-circle" />
        </template>
        <template #input-icon>
          <img src="/resources/images/icons/calendar.svg" width="24" height="24" />
        </template>
        <template #day="{ day }">
          <div class="dp__cell__dayValue">
            {{ day }}
          </div>
        </template>
        <template #arrow-right>
          <Icon name="arrow-right-circle" />
        </template>
      </VueDatePicker>
    </div>
  </Teleport>
</template>

<script setup>
  import VueDatePicker from '@vuepic/vue-datepicker';
  import { format, toZonedTime } from 'date-fns-tz';
  import countriesList from '~/data/countries';

  defineOptions({
    auth: 'aminess'
  });

  const props = defineProps({
    properties: {
      type: Array,
      default: [],
    },
  });

  const emit = defineEmits(['formSubmitted']);

  const { link } = useSetup();
  const { user: userData } = useAuth('aminess');
  const { t } = useI18n();

  const field = ref([]);
  const conditionalField = ref([]);
  const isButtonDisabled = ref(true);
  const isLoading = ref(false);
  const terms = ref(false);
  const period = ref(null);
  const dateModal = ref(false);
  const datepickerDates = ref([]);

  const phoneNumber = ref({
    phone_number: '',
    dial_code: '+385',
  });

  const adultOptions = computed(() => {
    let result = [];
    for (let i = 1; i <= 10; i++) result.push({ value: i, text: t('availabilityContact.guests', i) });
    return result;
  });

  const childrenOptions = computed(() => {
    let result = [];
    for (let i = 0; i <= 10; i++) result.push({ value: i, text: t('availabilityContact.guests', i) });
    return result;
  });

  const children = ref(childrenOptions.value[0].value);

  const childrenAge = computed(() => {
    let result = [];
    for (let i = 1; i <= 16; i++) result.push({ value: i });
    return result;
  });

  const formData = ref({
    first_name: '',
    last_name: '',
    email: '',
    confirm_email: '',
    phone: '',
    accommodation: '',
    comment: '',
    address: '',
    city: '',
    country: '',
    zip: '',
    adults_count: adultOptions.value[0].value,
    date_from: '',
    date_to: '',
    children_age: [],
  });
  const formErrors = ref({});

  function addField(el) {
    if (el && !el?.added) {
      field.value.push(el);
      el.added = true;
    }
  }

  function addConditionalField(el) {
    if (el && !el?.added) {
      conditionalField.value.push(el);
      el.added = true;
    }
  }

  function toggleDateModal() {
    dateModal.value = !dateModal.value;
    document.documentElement.classList[dateModal.value ? 'add' : 'remove']('isOverflow');
  }

  function formatDatepickerDate(date) {
    return `${formatDate(date[0])} - ${formatDate(date[1])}`;
  }

  function updateDates(dates) {
    period.value = formatDatepickerDate(dates);
    const jsDates = dates.map((date) => format(toZonedTime(new Date(date), 'Europe/Zagreb'), 'yyyy-MM-dd', { timeZone: 'Europe/Zagreb' }));
    formData.value.date_from = jsDates[0];
    formData.value.date_to = jsDates[1];

    toggleDateModal();
  }

  function scrollToError() {
    setTimeout(() => {
      const elements = document.querySelectorAll('.formField.isError');
      if (elements.length) elements[0].scrollIntoView({ behavior: 'smooth' });
    }, 10);
  }

  async function submitForm() {
    let isValid = useFormIsValid(field.value);
    if (!isValid) {
      scrollToError();
      return;
    }
    isButtonDisabled.value = true;
    isLoading.value = true;
    try {
      const response = await useClientFetch(`/availability-enquiry/accommodation/`, {
        method: 'POST',
        body: JSON.stringify(formData.value),
      });
      emit('formSubmitted');
    }
    catch(e) {
      console.dir(e);
      if (e.data) formErrors.value = e.data;
      if (formErrors.value?.details?.date_from && formErrors.value?.details?.date_to) {
        formErrors.value = { ...formErrors.value, period: t('formErrors.required') };
      }
      if(e.statusCode >= 500 || e.statusCode === undefined) {
        showError({
          statusCode: e.statusCode,
          statusMessage: 'Error',
        });
        window.scrollTo(0, 0);
      }
      scrollToError();
    }
    isButtonDisabled.value = false;
    isLoading.value = false;
  }

  watch(() => phoneNumber.value, (value) => {
    if (value.dial_code && value.phone_number) {
      formData.value.phone = `${value.dial_code}${value.phone_number}`;
    } else {
      formData.value.phone = '';
    }
  }, { deep: true, immediate: true });

  watch(() => terms.value, (value) => {
    isButtonDisabled.value = value ? false : true
  }, { deep: true, immediate: true });
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_reservationPanel.scss');
  @import url('@/assets/styles/components/_datePicker.scss');
</style>